import { Component, type ErrorInfo, type ReactNode } from 'react';
import { layoutMeasures } from '@/theme/layoutMeasures';
import { Box, Inline, PageShell, Stack, StatusBanner, Surface, Text } from '@/ui';
import { Button } from '@/ui/primitives/Button';

type AppErrorBoundaryState = { hasError: boolean; message?: string };

export class AppErrorBoundary extends Component<{ children: ReactNode }, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // UI fallback is handled by getDerivedStateFromError.
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: layoutMeasures.viewport.fullHeight,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <PageShell maxWidth="sm" paddingX="lg" paddingY="lg">
            <Surface padding="lg" border={false}>
              <Stack gap="md" align="center">
                <StatusBanner
                  tone="red"
                  message="Something went wrong. Please reload and try again."
                />
                {this.state.message ? (
                  <Text role="body" color="textSecondary">
                    {this.state.message}
                  </Text>
                ) : null}
                <Inline gap="sm" align="center" justify="center">
                  <Button
                    variant="primary"
                    onClick={() => {
                      window.location.reload();
                    }}
                  >
                    Reload
                  </Button>
                </Inline>
                <Text role="body" color="textSecondary">
                  If this persists, reload the page and try again.
                </Text>
              </Stack>
            </Surface>
          </PageShell>
        </Box>
      );
    }
    return this.props.children;
  }
}
